import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'

export default function BuildView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [build, setBuild] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`/api/builds/${id}`)
      .then(response => response.json())
      .then(data => {
        setBuild(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching build:', err)
        setLoading(false)
      })
  }, [id])

  const deleteBuild = () => {
    if (!confirm('Are you sure you want to delete this build? This will restore the parts to inventory.')) return

    fetch(`/api/builds/${id}`, { method: 'DELETE' })
      .then(response => {
        if (response.ok) {
          navigate('/builds')
        } else {
          return response.json().then(data => {
            alert(data.errors?.join(', ') || 'Failed to delete build')
          })
        }
      })
      .catch(err => console.error('Error deleting build:', err))
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (!build) {
    return (
      <div className="px-4 sm:px-0">
        <div className="text-center py-12">
          <p className="text-gray-500">Build not found</p>
          <Link to="/builds" className="mt-4 text-gray-600 hover:text-gray-900">
            &larr; Back to Builds
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0 max-w-4xl mx-auto">
      <div className="mb-6 flex justify-between items-center">
        <Link to="/builds" className="text-gray-600 hover:text-gray-900">
          &larr; Back to Builds
        </Link>
        <button
          onClick={deleteBuild}
          className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
        >
          Delete Build
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {build.configuration_name}
          </h1>
          <p className="text-gray-600">
            Completed on {formatDate(build.completed_at)}
          </p>
        </div>

        {/* Details */}
        <div className="p-6 border-b">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-500">Configuration</p>
              <p className="font-medium">{build.configuration_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Options</p>
              <p className="font-medium">
                {build.option_names && build.option_names.length > 0
                  ? build.option_names.join(', ')
                  : 'None'
                }
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Van Serial Number</p>
              <p className="font-medium">{build.van_serial_number || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Cost</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(build.total_cost)}</p>
            </div>
          </div>

          {build.notes && (
            <div className="mt-6">
              <p className="text-sm text-gray-500">Notes</p>
              <p className="mt-1 text-gray-900">{build.notes}</p>
            </div>
          )}
        </div>

        {/* Parts Used */}
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Parts Used</h2>
          {build.parts && build.parts.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Line Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {build.parts.map((part, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <Link
                        to={`/parts/${part.part_id}`}
                        className="hover:underline"
                      >
                        {part.description}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{part.quantity_used}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatCurrency(part.price_at_build)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatCurrency(part.line_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="3" className="px-4 py-2 text-sm font-bold text-gray-900 text-right">
                    Total:
                  </td>
                  <td className="px-4 py-2 text-sm font-bold text-gray-900">
                    {formatCurrency(build.total_cost)}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <p className="text-gray-500">No parts recorded</p>
          )}
        </div>
      </div>
    </div>
  )
}
